import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Divider, Stack, useTheme } from "@vapor/react-material";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FulfilmentStatus } from "../../../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import { useGetKPIs } from "../../../../../core/api/use-get-kpis/useGetKPIs";
import { FulfilmentListHeaderKPICard } from "../fulfilment-list-header-kpi-card/FulfilmenListHeaderKPICard";

interface FulfilmentListHeaderKPIsProps {
    accordionRight?: JSX.Element;
    accordionLeft?: JSX.Element;
    statuses: FulfilmentStatus[];
    onChangeSelectedKPIs: (selectedKPIs: FulfilmentStatus[]) => unknown;
}

export const FulfilmentListHeaderKPIs = ({
    accordionRight,
    accordionLeft,
    statuses,
    onChangeSelectedKPIs,
}: FulfilmentListHeaderKPIsProps): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { data: kpis, isFetching: loadingKpis } = useGetKPIs();

    return (
        <>
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
            >
                <Stack direction="row" spacing={2}>
                    <Stack direction="row" spacing={1}>
                        <LoadingWrapper
                            loading={loadingKpis}
                            spinnerProps={{ loadingText: "" }}
                            variant="spinner"
                        >
                            <Typography
                                color={theme.palette.primary.textTitleColor}
                                fontSize={24}
                                variant="bodyLarge500"
                            >
                                {kpis?.statusCounters
                                    ?.map((kpi) => kpi.count)
                                    .reduce((acc, curr) => acc + curr, 0) ?? 0}
                            </Typography>
                        </LoadingWrapper>
                        <Typography
                            color={theme.palette.primary.textTitleColor}
                            fontSize={20}
                            ml={1}
                            variant="bodyLarge"
                        >
                            {t(
                                "views.home.components.fulfilment_list_header.kpis.accordion",
                            )}
                        </Typography>

                        <Divider orientation="vertical" light flexItem />

                        <LoadingWrapper
                            loading={loadingKpis}
                            spinnerProps={{ loadingText: "" }}
                            variant="spinner"
                        >
                            <Typography
                                color={theme.palette.primary.textTitleColor}
                                fontSize={24}
                                variant="bodyLarge500"
                            >
                                {kpis?.uniqueTaxPayersCount ?? 0}
                            </Typography>
                        </LoadingWrapper>
                        <Typography
                            color={theme.palette.primary.textTitleColor}
                            fontSize={20}
                            ml={1}
                            variant="bodyLarge"
                        >
                            {t(
                                "views.home.components.fulfilment_list_header.kpis.accordion.taxpayers",
                            )}
                        </Typography>
                    </Stack>
                </Stack>
                {accordionLeft}
                {accordionRight}
            </Stack>

            <Stack alignItems="end" direction="row" id="kpi-list" spacing={2}>
                {[
                    FulfilmentStatus.LINKED,
                    FulfilmentStatus.PENDING,
                    FulfilmentStatus.CLOSED,
                    FulfilmentStatus.COMPLETED,
                ].map((status) => (
                    <FulfilmentListHeaderKPICard
                        numberOfWarnings={
                            status === FulfilmentStatus.COMPLETED
                                ? (kpis?.completedWithWarnings ?? 0)
                                : 0
                        }
                        fulfilmentStatus={status}
                        isSelected={statuses.indexOf(status) > -1}
                        loading={loadingKpis}
                        key={status}
                        numberOfFulfilmentStatus={
                            kpis?.statusCounters
                                ?.filter((kpi) => kpi.status === status)
                                .map((kpi) => kpi.count)
                                .reduce((acc, curr) => acc + curr, 0) ?? 0
                        }
                        onClickKPI={(status) => {
                            const newSelectedKPIs = [...statuses];
                            const statusIndex = newSelectedKPIs.indexOf(status);

                            if (statusIndex === -1) {
                                newSelectedKPIs.push(status);
                            } else {
                                newSelectedKPIs.splice(statusIndex, 1);
                            }

                            onChangeSelectedKPIs(newSelectedKPIs);
                        }}
                    />
                ))}
            </Stack>
        </>
    );
};
